import React, { useState, useEffect } from "react";
import store from "../store";
import usePlan from "../hooks/usePlan";
import { QRCodeSVG } from "qrcode.react";

const PaymentWarning = ({ message }) => {
  const { isExpired, isDue, plan, formatAmount } = usePlan();
  const [timeLeft, setTimeLeft] = useState(10);
  const [isVisible, setIsVisible] = useState(true);
  const [showCloseButton, setShowCloseButton] = useState(false);

  // Generate UPI payment URL
  const generateUpiUrl = () => {
    const payeeVpa = "qrapid@ybl"; // Replace with your actual UPI ID
    const payeeName = "QRapid | POS";
    const amount = plan?.amount || 0;
    const note = `${store.restaurantInfo.id}, ${store.restaurantInfo.name}`;
    
    return `upi://pay?pa=${encodeURIComponent(payeeVpa)}&pn=${encodeURIComponent(payeeName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(note)}`;
  };

  useEffect(() => {
    let timer;
    if (isVisible && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    }

    if (timeLeft === 0) {
      setShowCloseButton(true);
    }

    return () => {
      clearInterval(timer);
    };
  }, [timeLeft, isVisible]);

  // Reset component state when it unmounts
  useEffect(() => {
    return () => {
      setTimeLeft(10);
      setIsVisible(true);
    };
  }, []);

  // Show warning only for expired plans
  // Don't show for due payments or active free trials
  if (!isExpired) return null;
  if (!isVisible) return null;

  return (
    <div className="payment-modal-overlay" style={{ zIndex: 1000 }}>
      {/* Overlay with blur effect */}
      <div className="payment-modal-blur"></div>
      
      {/* Modal */}
      <div className="payment-modal-container">
        {/* Timer and Close Button */}
        <div className="payment-modal-timer-container">
          {!showCloseButton && <span className="payment-modal-timer"> {timeLeft}s</span>}
          {showCloseButton && (
            <button 
              className="payment-modal-close-btn"
              onClick={() => setIsVisible(false)}
            >
              Close
            </button>
          )}
        </div>

        {/* Header with Warning */}
        <div className="payment-modal-header">
          <div className="payment-modal-icon-container">
            <svg
              className="payment-modal-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="payment-modal-title">
            {isExpired ? "Your Subscription Has Expired" : "Payment Due"}
          </h2>
        </div>

        {/* Content */}
        <div className="payment-modal-content">
          {/* Text Content */}
          <div className="payment-modal-text-container">
            <p className="payment-modal-primary-text">
              {isExpired 
                ? "To continue using QRapid's services, please complete your payment."
                : "Your payment is due. Please complete payment to avoid service interruption."
              }
            </p>
            <p className="payment-modal-secondary-text">
              {isExpired 
                ? "Your subscription has expired. All premium features will be paused until payment is completed!"
                : "Please update your payment method to avoid interruption of services."
              }
            </p>

            {/* Payment Details */}
            <div className="payment-modal-details">
              <div>
                <h3 className="payment-modal-details-label">Amount to be Paid:</h3>
                <p className="payment-modal-amount">
                  {plan?.amount ? formatAmount(plan.amount) : "Contact Admin"}
                </p>
              </div>
            </div>
          </div>

          {/* Dynamic UPI QR Code */}
          <div className="payment-modal-qr-container">
            <div className="payment-modal-qr-wrapper">
              <QRCodeSVG 
                value={generateUpiUrl()} 
                size={150}
                level="M"
                marginSize={4}
                bgColor="#FFFFFF"
                fgColor="#000000"
              />
              <p className="payment-modal-qr-text">
                Scan to pay {formatAmount(plan?.amount) || "Contact Admin"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentWarning;
